import { forwardRef } from 'react';

import { cn, Tooltip, TooltipContent, TooltipPortal, TooltipTrigger } from '@crossnokaye/ux-common';

import { Button, ButtonProps } from '@/ux-common/components/button/button';

interface TooltipButtonProps extends ButtonProps {
  delayDuration?: number;
  disabled?: boolean;
  showTooltipWhenEnabled?: boolean;
  tooltipContent?: string;
}

/**
 * Button component with conditional tooltip for disabled states
 */
export const TooltipButton = forwardRef<HTMLButtonElement, TooltipButtonProps>(
  (
    { tooltipContent, disabled, children, delayDuration, showTooltipWhenEnabled = false, ...props },
    ref
  ) => {
    // TODO: Replace all uses of TooltipButton with withTooltip from @crossnokaye/ux-common
    // Only show tooltip if:
    // 1. Tooltip content is provided, AND
    // 2. Either button is disabled OR showTooltipWhenEnabled is true
    const shouldShowTooltip = tooltipContent && (disabled || showTooltipWhenEnabled);

    if (!shouldShowTooltip) {
      return (
        <Button ref={ref} disabled={disabled} {...props}>
          {children}
        </Button>
      );
    }

    return (
      <Tooltip delayDuration={delayDuration}>
        <TooltipTrigger asChild>
          <span className={cn('leading-none')}>
            <Button ref={ref} disabled={disabled} {...props}>
              {children}
            </Button>
          </span>
        </TooltipTrigger>
        <TooltipPortal>
          <TooltipContent side="top" align="center" className="z-50">
            {tooltipContent}
          </TooltipContent>
        </TooltipPortal>
      </Tooltip>
    );
  }
);

TooltipButton.displayName = 'TooltipButton';
